/**
 * @fileoverview A photo that fills its frame, except a small one: that is
 * shown at its own size, centred, never enlarged (B32).
 *
 * The frame's parent supplies the size and a placeholder fill (a theme colour
 * such as bg-muted) for while the picture loads. A small picture floats on a
 * clear background: this sets data-small="true" on the frame, and the frame
 * drops its fill with data-[small=true]:bg-transparent (and any shadow with
 * data-[small=true]:shadow-none). A frame inside a <picture> is the picture's parent. The picture's
 * own size is read when it loads (naturalWidth, naturalHeight) and compared
 * with the frame's (photoFit), again when the frame is resized. Photos big
 * enough to fill the frame are drawn exactly as before (object-cover; or whole
 * with fit='contain', which also never enlarges).
 *
 * Where: the plant card's picture (PlantCardPhoto), the plant tile and row, the
 * Archived thumbnail, the public garden and share pages (gardenPageParts,
 * gardenPageSections), the packet thumbnails (PacketThumb) and the tray's piles,
 * the first-share panel (ShareMoments), the public "More from" tiles
 * (GardenItemPage), the gardens directory (gardensPageParts) and bed covers
 * (LocationTile, LocationRow, LocationCard).
 * API: none.
 *
 * @module FramedPhoto
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { isSmallerThanFrame } from '@/shared/lib/photoFit';

/** What FramedPhoto takes: an img's own props. */
export interface FramedPhotoProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  /**
   * How a picture big enough for the frame is drawn: 'cover' fills it (the
   * default); 'contain' shows it whole. Either way a small one sits at its
   * own size, centred.
   */
  fit?: 'cover' | 'contain';
}

/**
 * The element that is the photo's frame: its parent, or a <picture>'s parent.
 *
 * @param img - The img element, or null before it mounts.
 * @returns The frame, or null.
 */
function frameOf(img: HTMLImageElement | null): HTMLElement | null {
  if (!img) return null;
  const parent = img.parentElement;
  return parent?.tagName === 'PICTURE' ? parent.parentElement : parent;
}

/**
 * The photo, filling its relatively-positioned parent or, when too small for
 * that, sitting at its own size in the middle of it.
 *
 * @param props - The img's props; className is added to, onLoad is still called.
 */
export const FramedPhoto: React.FC<FramedPhotoProps> = ({ className, onLoad, fit = 'cover', ...rest }) => {
  /** The img element, for its natural and shown sizes. */
  const ref = React.useRef<HTMLImageElement>(null);
  /** True once the picture is known to be smaller than its frame. */
  const [small, setSmall] = React.useState(false);

  /** Compares the picture's own size with its frame's, which is the parent's. */
  const measure = React.useCallback(() => {
    const img = ref.current;
    const frame = frameOf(img);
    if (!img || !frame) return;
    setSmall(isSmallerThanFrame(
      { width: img.naturalWidth, height: img.naturalHeight },
      { width: frame.clientWidth, height: frame.clientHeight },
    ));
  }, []);

  // The frame wears the state, so it can go clear around a small picture.
  React.useEffect(() => {
    const frame = frameOf(ref.current);
    if (!frame) return undefined;
    frame.dataset.small = small ? 'true' : 'false';
    return () => { delete frame.dataset.small; };
  }, [small]);

  React.useEffect(() => {
    const frame = frameOf(ref.current);
    if (!frame || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [measure]);

  return (
    <img
      ref={ref}
      {...rest}
      onLoad={(event) => { measure(); onLoad?.(event); }}
      className={cn(
        'absolute',
        // Contain never enlarges either: natural size, shrunk only to fit.
        small || fit === 'contain' ? 'inset-0 m-auto h-auto w-auto max-h-full max-w-full' : 'inset-0 h-full w-full object-cover',
        className,
      )}
    />
  );
};
